/*
  Iter122k137 · Editor testuale delle Guide (super_admin only = Studio Izzo).

  Componente modal che consente al SuperAdmin di modificare titolo / sottotitolo /
  capitoli di una guida. La modifica è persistita su MongoDB via l'API
  /api/guide/editabili/{id}. Un pulsante "Ripristina" torna al contenuto estratto
  dal PDF statico originale.
*/
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, X, Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronUp, FileText, ImagePlus, Image as ImageIcon } from "lucide-react";

export function GuideEditorModal({ gid, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/guide/editabili/${gid}`);
      setDoc(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Caricamento fallito");
      onClose();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gid]);

  const patchCap = (i, patch) => {
    setDoc((d) => ({ ...d, capitoli: d.capitoli.map((c, k) => k === i ? { ...c, ...patch } : c) }));
  };
  const removeCap = (i) => {
    if (!window.confirm("Eliminare questo capitolo dalla guida?")) return;
    setDoc((d) => ({ ...d, capitoli: d.capitoli.filter((_, k) => k !== i) }));
  };
  const addCap = () => {
    setDoc((d) => {
      const next = String((d.capitoli || []).length + 1);
      const capitoli = [...(d.capitoli || []), { numero: next, titolo: "Nuovo capitolo", testo: "" }];
      return { ...d, capitoli };
    });
    setOpenIdx((doc?.capitoli?.length) || 0);
  };
  const moveCap = (i, dir) => {
    setDoc((d) => {
      const arr = [...d.capitoli];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, capitoli: arr };
    });
  };

  const salva = async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const payload = {
        titolo: doc.titolo,
        sottotitolo: doc.sottotitolo,
        kicker: doc.kicker,
        capitoli: doc.capitoli.map((c) => ({
          numero: String(c.numero || ""),
          titolo: c.titolo || "",
          testo: c.testo || "",
          immagini: (c.immagini || []).map((im) => ({
            id: im.id,
            storage_path: im.storage_path,
            content_type: im.content_type || "image/png",
            caption: im.caption || "",
          })),
        })),
      };
      await api.put(`/guide/editabili/${gid}`, payload);
      toast.success("Guida aggiornata");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e) || "Salvataggio fallito"); }
    finally { setBusy(false); }
  };

  // Iter122k138 · upload immagine per capitolo
  const uploadImg = async (capIdx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5 MB)");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", "");
      const { data } = await api.post(`/guide/editabili/${gid}/immagini`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc((d) => ({
        ...d,
        capitoli: d.capitoli.map((c, k) => k === capIdx
          ? { ...c, immagini: [...(c.immagini || []), { ...data, caption: "" }] }
          : c),
      }));
      toast.success("Immagine caricata (ricorda di salvare)");
    } catch (e) { toast.error(formatApiError(e) || "Upload fallito"); }
    finally { setBusy(false); }
  };

  const removeImg = (capIdx, imgId) => {
    setDoc((d) => ({
      ...d,
      capitoli: d.capitoli.map((c, k) => k === capIdx
        ? { ...c, immagini: (c.immagini || []).filter((im) => im.id !== imgId) }
        : c),
    }));
  };

  const updateImgCaption = (capIdx, imgId, caption) => {
    setDoc((d) => ({
      ...d,
      capitoli: d.capitoli.map((c, k) => k === capIdx
        ? { ...c, immagini: (c.immagini || []).map((im) => im.id === imgId ? { ...im, caption } : im) }
        : c),
    }));
  };

  const ripristina = async () => {
    if (!window.confirm("Ripristinare il contenuto originale? Le modifiche salvate andranno perse.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/guide/editabili/${gid}`);
      setDoc(data);
      toast.success("Contenuto ripristinato");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e) || "Ripristino fallito"); }
    finally { setBusy(false); }
  };

  const anteprima = () => {
    // Iter122k144 · Il blob URL DEVE avere MIME "application/pdf" altrimenti
    // i browser mobili aprono il file come testo (mostrano il sorgente %PDF-1.4...).
    api.get(`/guide/editabili/${gid}/pdf`, { responseType: "blob" }).then((r) => {
      const blob = new Blob([r.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener");
    }).catch(() => toast.error("Anteprima non disponibile"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-stretch sm:items-start justify-center sm:p-4"
         data-testid="guide-editor-modal">
      <div className="bg-white w-full max-w-4xl sm:rounded-xl border-2 border-slate-200 shadow-xl flex flex-col max-h-screen sm:max-h-[calc(100vh-2rem)]">
        <div className="flex items-center justify-between gap-2 p-4 border-b-2 border-slate-100 shrink-0">
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg text-slate-900 truncate">
              Modifica testo · {doc?.titolo || gid}
            </h2>
            <p className="text-xs text-slate-500">
              Riservato all'Admin dello Studio Izzo. Le modifiche sono immediate e sostituiscono il PDF originale.
            </p>
          </div>
          <button onClick={onClose} data-testid="guide-editor-close"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !doc ? (
          <div className="flex items-center justify-center p-10 flex-1">
            <Loader2 className="w-6 h-6 animate-spin text-brand" />
          </div>
        ) : (
          <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Iter122k145 · Avviso onesto sul downgrade estetico rispetto al PDF originale curato */}
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex gap-2"
                 data-testid="guide-editor-warning-estetica">
              <span className="text-amber-600 shrink-0">⚠️</span>
              <div>
                <b>Attenzione — layout semplificato al salvataggio.</b> Il PDF originale
                contiene fasce colorate, callout ed elenchi stilizzati che il rigeneratore
                non è in grado di preservare. Gli <b>screenshot originali sono importati
                automaticamente</b> capitolo per capitolo e vengono re-inseriti nel PDF
                salvato. Puoi aggiungere/rimuovere immagini per ogni capitolo qui sotto.
              </div>
            </div>
            {doc.has_override && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 py-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Versione modificata attiva · ultimo salvataggio da <b className="ml-1">{doc.updated_by_name || "—"}</b>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Titolo</Label>
                <Input data-testid="guide-editor-titolo" value={doc.titolo || ""}
                  onChange={(e) => setDoc({ ...doc, titolo: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase">Sottotitolo</Label>
                <Input data-testid="guide-editor-sottotitolo" value={doc.sottotitolo || ""}
                  onChange={(e) => setDoc({ ...doc, sottotitolo: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase">Capitoli ({doc.capitoli.length})</Label>
                <Button size="sm" variant="outline" onClick={addCap} data-testid="guide-editor-aggiungi-cap"
                  className="border-brand text-brand hover:bg-brand/10">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi capitolo
                </Button>
              </div>
              <div className="space-y-2">
                {doc.capitoli.map((c, i) => {
                  const open = openIdx === i;
                  return (
                    <div key={i} className="rounded-lg border-2 border-slate-200 bg-slate-50"
                         data-testid={`guide-editor-cap-${i}`}>
                      <div className="flex items-center gap-2 p-2">
                        <button type="button" onClick={() => setOpenIdx(open ? -1 : i)}
                          className="flex-1 flex items-center gap-2 text-left min-w-0">
                          {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                          <span className="font-mono text-xs bg-white border border-slate-300 px-1.5 py-0.5 rounded">
                            {c.numero}.
                          </span>
                          <span className="font-semibold text-sm truncate">{c.titolo}</span>
                        </button>
                        <button type="button" onClick={() => moveCap(i, -1)} disabled={i === 0}
                          className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => moveCap(i, +1)} disabled={i === doc.capitoli.length - 1}
                          className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => removeCap(i)}
                          data-testid={`guide-editor-cap-del-${i}`}
                          className="p-1 rounded hover:bg-red-100 text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {open && (
                        <div className="p-3 pt-1 space-y-2 bg-white rounded-b-lg">
                          <div className="grid sm:grid-cols-6 gap-2">
                            <div className="sm:col-span-1">
                              <Label className="text-[10px] uppercase">Numero</Label>
                              <Input value={c.numero || ""} data-testid={`guide-editor-cap-num-${i}`}
                                onChange={(e) => patchCap(i, { numero: e.target.value })}
                                className="mt-1 h-9 text-sm" />
                            </div>
                            <div className="sm:col-span-5">
                              <Label className="text-[10px] uppercase">Titolo capitolo</Label>
                              <Input value={c.titolo || ""} data-testid={`guide-editor-cap-titolo-${i}`}
                                onChange={(e) => patchCap(i, { titolo: e.target.value })}
                                className="mt-1 h-9 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase">
                              Testo (separa i paragrafi con una riga vuota)
                            </Label>
                            <Textarea value={c.testo || ""} rows={10}
                              data-testid={`guide-editor-cap-testo-${i}`}
                              onChange={(e) => patchCap(i, { testo: e.target.value })}
                              className="mt-1 font-mono text-xs" />
                          </div>
                          <ImmaginiCapitolo capIdx={i} gid={gid}
                            immagini={c.immagini || []}
                            onUpload={uploadImg}
                            onRemove={removeImg}
                            onCaption={updateImgCaption}
                            disabled={busy} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="shrink-0 flex flex-wrap items-center gap-2 p-3 sm:p-4 border-t-2 border-slate-100 bg-slate-50 sm:rounded-b-xl">
          <Button variant="outline" onClick={ripristina} disabled={busy || loading}
            data-testid="guide-editor-reset"
            className="border-slate-300 text-slate-700 hover:bg-slate-100 order-3 sm:order-1 sm:mr-auto">
            <RotateCcw className="w-4 h-4 mr-1" /> Ripristina originale
          </Button>
          <Button variant="outline" onClick={anteprima} disabled={busy || loading}
            data-testid="guide-editor-anteprima"
            className="order-2 flex-1 sm:flex-none">
            <FileText className="w-4 h-4 mr-1" /> Anteprima PDF
          </Button>
          <Button onClick={salva} disabled={busy || loading}
            data-testid="guide-editor-submit"
            className="bg-brand hover:bg-brand-dark order-1 sm:order-3 flex-1 sm:flex-none">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Salva modifiche
          </Button>
        </div>
      </div>
    </div>
  );
}


// Iter122k138 · sotto-componente per la gestione immagini del singolo capitolo.
// Le miniature sono servite via endpoint `/api/guide/editabili/{gid}/immagini/{img_id}`
// con auth Bearer (utilizziamo il client `api` per ottenere un blob e generare un objectURL).
function ImmaginiCapitolo({ capIdx, gid, immagini, onUpload, onRemove, onCaption, disabled }) {
  const fileRef = useRef(null);
  const [thumbs, setThumbs] = useState({}); // img_id -> objectURL

  useEffect(() => {
    let cancelled = false;
    const missing = immagini.filter((im) => im.id && !thumbs[im.id]);
    missing.forEach((im) => {
      api.get(`/guide/editabili/${gid}/immagini/${im.id}`, { responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          const url = window.URL.createObjectURL(new Blob([r.data]));
          setThumbs((t) => ({ ...t, [im.id]: url }));
        })
        .catch(() => {/* silenzioso: mostra placeholder */});
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immagini.map((i) => i.id).join(",")]);

  return (
    <div data-testid={`guide-editor-cap-immagini-${capIdx}`}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[10px] uppercase flex items-center gap-1">
          <ImageIcon className="w-3.5 h-3.5" /> Immagini ({immagini.length})
        </Label>
        <label className="cursor-pointer">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
            data-testid={`guide-editor-cap-upload-${capIdx}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              onUpload(capIdx, f);
              if (fileRef.current) fileRef.current.value = "";
            }}
            className="hidden" disabled={disabled} />
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border-2 ${disabled ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400" : "border-brand text-brand hover:bg-brand/10"}`}>
            <ImagePlus className="w-3.5 h-3.5" /> Aggiungi immagine
          </span>
        </label>
      </div>
      {immagini.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">Nessuna immagine. Ammessi PNG/JPG/WebP/GIF · max 5 MB.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {immagini.map((im) => (
            <div key={im.id} className="rounded-md border-2 border-slate-200 bg-slate-50 p-1.5 flex flex-col gap-1"
                 data-testid={`guide-editor-img-${im.id}`}>
              <div className="aspect-video bg-white rounded flex items-center justify-center overflow-hidden">
                {thumbs[im.id] ? (
                  <img src={thumbs[im.id]} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
                )}
              </div>
              <Input value={im.caption || ""}
                data-testid={`guide-editor-img-caption-${im.id}`}
                onChange={(e) => onCaption(capIdx, im.id, e.target.value)}
                placeholder="Didascalia (opz.)"
                className="h-7 text-[11px]" />
              <button type="button" onClick={() => onRemove(capIdx, im.id)}
                data-testid={`guide-editor-img-del-${im.id}`}
                className="inline-flex items-center justify-center gap-1 text-[10px] font-semibold text-red-600 hover:text-red-800">
                <Trash2 className="w-3 h-3" /> Rimuovi
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
